'use client';

import { Button } from '@common/components';
import { SvgTabMoreLeft, SvgTabMoreRight } from '@common/icon';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';

import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';

import type { WelloPickItem } from '../../types';
import { PickedGift } from './PickedGift';

interface WelloPickSectionProps {
  items: WelloPickItem[];
  className?: string;
}

export const WelloPickSection = ({
  items,
  className,
}: WelloPickSectionProps) => {
  const [isSwiperReady, setIsSwiperReady] = useState(false);
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP);

  //* embla carousel 인스턴스 참조
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: items.length > 1,
    align: 'start',
    dragFree: isDesktopViewport ? false : true,
  });

  const handleClickItem = (item: WelloPickItem) => {
    trackGtmEvent(GTM_EVENT_TYPE.SELECT_ITEM, {
      currency: 'KRW',
      value: item.donationAmount,
      local_name: item.institutionName,
      items: [
        {
          item_type: '답례품',
          item_list_name: '홈 오늘의 웰로 Pick 섹션',
          item_list_id: item.productNumber,
          price: item.donationAmount,
          item_name: item.productName,
          quantity: 1,
          item_id: item.productNumber,
        },
      ],
    });
  };

  //* embla 인스턴스가 준비되면 화면에 표시
  useEffect(() => {
    if (!emblaApi) return;
    setIsSwiperReady(true);
  }, [emblaApi]);

  const isNotRender = items.length < 1;

  if (isNotRender) return null;

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-24 px-40 content-container',
        'mobile-tablet:items-start mobile-tablet:gap-16 mobile-tablet:px-0',
        className,
      )}
    >
      <header
        className={cn(
          'flex items-center gap-10',
          'mobile-tablet:gap-4 mobile-tablet:px-20',
        )}
      >
        <h2
          className={cn(
            'font-800 text-Gray-900 typo-h4',
            'mobile-tablet:font-700 mobile-tablet:typo-body1',
          )}
        >
          👀 지금 많이 조회하는 답례품
        </h2>
      </header>

      <div className="relative w-fit max-w-full">
        <div
          ref={emblaRef}
          className={cn(
            'w-fit max-w-full overflow-hidden transition-opacity duration-300',
            'mobile-tablet:px-20',
            {
              'opacity-0': !isSwiperReady,
              'opacity-100': isSwiperReady,
            },
          )}
        >
          <div className="flex">
            {items.map((item) => {
              const isDisplayInstitution =
                !!item.institutionId &&
                !!item.institutionName &&
                !!item.institutionLogoUrl;

              return (
                <div
                  key={item.productNumber}
                  className="mr-12 !w-fit flex-[0_0_auto]"
                >
                  <PickedGift
                    donationAmount={item.donationAmount}
                    onClickItem={() => handleClickItem(item)}
                    isDisplayInstitution={isDisplayInstitution}
                    imageUrl={item.imageUrl}
                    institutionId={item.institutionId}
                    institutionName={item.institutionName}
                    institutionLogoUrl={item.institutionLogoUrl}
                    isNew={item.isNew}
                    readCount={item.readCount}
                    productNumber={item.productNumber}
                    productName={item.productName}
                    reviewRating={item.reviewRating}
                    reviewCount={item.reviewCount}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Button
          theme="sub-text"
          className={cn(
            'absolute -left-26 top-170 z-10 size-fit -translate-y-1/2 p-0',
            'mobile-tablet:hidden',
          )}
          onClick={() => emblaApi?.scrollPrev()}
        >
          <SvgTabMoreLeft className="size-52" originalFill />
        </Button>
        <Button
          theme="sub-text"
          className={cn(
            'absolute -right-26 top-170 z-10 size-fit -translate-y-1/2 p-0',
            'mobile-tablet:hidden',
          )}
          onClick={() => emblaApi?.scrollNext()}
        >
          <SvgTabMoreRight className="size-52" originalFill />
        </Button>
      </div>
    </section>
  );
};
